import { useEffect, useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'
import { AccountLayout } from '@/components/account/AccountLayout'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { PhoneField } from '@/components/auth/PhoneField'
import { useAuth } from '@/lib/auth'
import { updateUserProfile, useUserProfile } from '@/lib/user-profile'

export function AccountProfilePage() {
  const { user, loading: authLoading } = useAuth()
  const { profile, loading, error, refresh } = useUserProfile()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => {
    if (!profile) return
    /* eslint-disable react-hooks/set-state-in-effect */
    setFirstName(profile.first_name ?? '')
    setLastName(profile.last_name ?? '')
    setPhone(profile.phone ?? '')
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [profile])

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!user) return
    if (phone.trim() && !isValidPhoneNumber(phone.trim())) {
      setFeedback({ kind: 'err', text: 'Phone number is not valid.' })
      return
    }
    setSaving(true)
    setFeedback(null)
    const { error: err } = await updateUserProfile(user.id, {
      first_name: firstName.trim() || null,
      last_name: lastName.trim() || null,
      phone: phone.trim() || null,
    })
    setSaving(false)
    if (err) setFeedback({ kind: 'err', text: err })
    else {
      setFeedback({ kind: 'ok', text: 'Saved.' })
      void refresh()
    }
  }

  if (!authLoading && !user) return <Navigate to="/sign-in" replace />

  return (
    <AccountLayout>
      <header className="mb-8">
        <h1 className="text-[26px] font-semibold tracking-tight text-[var(--color-text-strong)]">
          Profile
        </h1>
        <p className="mt-1 text-[14px] text-[var(--color-muted)]">
          Used for account contact and how we greet you.
        </p>
      </header>
      <ProfileRedirectBanner />

      {loading ? (
        <p className="text-[13px] text-[var(--color-muted)]">Loading…</p>
      ) : error ? (
        <p className="text-[13px] text-[var(--color-signal)]">{error}</p>
      ) : (
        <section className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-6">
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input id="first-name" label="First name" value={firstName} onChange={setFirstName} autoComplete="given-name" />
              <Input id="last-name" label="Last name" value={lastName} onChange={setLastName} autoComplete="family-name" />
            </div>
            <PhoneField
              id="phone"
              label="Phone"
              value={phone}
              onChange={setPhone}
              required={false}
              hint="Optional"
            />
            <Input
              id="email"
              label="Email"
              type="email"
              value={user?.email ?? ''}
              onChange={() => {}}
              readOnly
              hint="Read-only — contact support to change."
            />
            <div className="flex items-center justify-between gap-3">
              {feedback ? (
                <span
                  className={[
                    'text-[12.5px]',
                    feedback.kind === 'ok'
                      ? 'text-[var(--color-accent)]'
                      : 'text-[var(--color-signal)]',
                  ].join(' ')}
                >
                  {feedback.text}
                </span>
              ) : (
                <span />
              )}
              <SubmitButton submitting={saving}>Save changes</SubmitButton>
            </div>
          </form>
        </section>
      )}
    </AccountLayout>
  )
}

function ProfileRedirectBanner() {
  const location = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)
  const message = params.get('confirmed') === '1' ? 'Your email is confirmed. Welcome to GeoBorder.' : null

  useEffect(() => {
    if (!message) return
    const timer = window.setTimeout(() => {
      params.delete('confirmed')
      const next = params.toString()
      navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true })
    }, 5000)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message])

  if (!message) return null
  return (
    <div
      role="status"
      className={[
        'mb-6 flex items-center gap-3',
        'rounded-[var(--radius-DEFAULT)] border border-[var(--color-accent)]/40',
        'bg-[var(--color-accent-soft)] px-4 py-3',
        'text-[13.5px] text-[var(--color-text-strong)]',
      ].join(' ')}
    >
      <CheckCircle2 size={16} className="text-[var(--color-accent)] shrink-0" aria-hidden />
      <span>{message}</span>
    </div>
  )
}

// ----- shared form primitives ------------------------------------------------
// Inlined here rather than extracted to keep this page self-contained;
// the parallel security page uses the same shapes.

interface InputProps {
  id: string
  label: string
  type?: 'text' | 'email' | 'password' | 'tel'
  value: string
  onChange: (value: string) => void
  required?: boolean
  minLength?: number
  autoComplete?: string
  readOnly?: boolean
  hint?: string
}
function Input({
  id,
  label,
  type = 'text',
  value,
  onChange,
  required = true,
  minLength,
  autoComplete,
  readOnly = false,
  hint,
}: InputProps) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1.5">
      <span className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
          {label}
        </span>
        {hint ? (
          <span className="text-[11px] font-normal normal-case text-[var(--color-muted)]/80">
            {hint}
          </span>
        ) : null}
      </span>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required && !readOnly}
        minLength={minLength}
        readOnly={readOnly}
        className={[
          'w-full px-3 py-2.5 rounded-[var(--radius-DEFAULT)]',
          'bg-[var(--color-surface)]',
          'border border-[var(--color-border)]',
          'text-[14px] text-[var(--color-text)]',
          'placeholder:text-[var(--color-muted)]/60',
          'focus:outline-none focus:border-[var(--color-accent)]',
          'focus:ring-2 focus:ring-[var(--color-accent)]/30',
          'transition-colors',
          readOnly ? 'opacity-70 cursor-not-allowed' : '',
        ].join(' ')}
      />
    </label>
  )
}

interface SubmitButtonProps { children: React.ReactNode; submitting: boolean }
function SubmitButton({ children, submitting }: SubmitButtonProps) {
  return (
    <button
      type="submit"
      disabled={submitting}
      className={[
        'inline-flex items-center justify-center gap-2',
        'px-4 py-2 rounded-[var(--radius-DEFAULT)]',
        'bg-[var(--color-accent)] text-[var(--color-surface)]',
        'text-[13px] font-medium',
        'hover:bg-[var(--color-accent-hover)] transition-colors',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        'cursor-pointer',
      ].join(' ')}
    >
      {submitting ? 'Working…' : children}
    </button>
  )
}
